import { isMax } from './detect';

export const PROVIDER = 'telegram';

const TELEGRAM_SDK_URL = 'https://telegram.org/js/telegram-web-app.js';

let sdkLoadPromise = null;

export function loadTelegramSdk() {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  if (isMax()) {
    return Promise.resolve();
  }
  if (window.Telegram?.WebApp) {
    return Promise.resolve();
  }
  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }

  sdkLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${TELEGRAM_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Telegram SDK failed to load')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.src = TELEGRAM_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Telegram SDK failed to load'));
    document.head.appendChild(script);
  });

  return sdkLoadPromise;
}

export function isAvailable() {
  if (isMax()) {
    return false;
  }
  return Boolean(window.Telegram?.WebApp);
}

export async function ensureReady() {
  if (isMax()) {
    return null;
  }
  await loadTelegramSdk();
  return isAvailable() ? window.Telegram.WebApp : null;
}

export function init() {
  if (isMax()) {
    return null;
  }

  const tg = window.Telegram?.WebApp;
  if (!tg) {
    return null;
  }

  tg.ready();
  tg.expand();

  if (typeof tg.disableVerticalSwipes === 'function') {
    tg.disableVerticalSwipes();
  }

  return tg;
}

export function getAuthPayload() {
  if (isMax()) {
    return null;
  }
  return window.Telegram?.WebApp?.initData || null;
}

export function hasAuthPayload() {
  if (isMax()) {
    return false;
  }
  const payload = window.Telegram?.WebApp?.initData;
  return Boolean(payload && /(?:^|&)user=/.test(payload));
}

function readStartParamFromUrl() {
  if (typeof window === 'undefined') {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has('startapp')) {
    return searchParams.get('startapp');
  }

  const hash = window.location.hash?.slice(1) || '';
  if (!hash) {
    return null;
  }

  const hashParams = new URLSearchParams(hash);
  if (hashParams.has('startapp')) {
    return hashParams.get('startapp');
  }

  return null;
}

export function getStartParam() {
  if (isMax()) {
    return null;
  }
  const tg = window.Telegram?.WebApp;
  if (tg?.initDataUnsafe?.start_param) {
    return tg.initDataUnsafe.start_param;
  }
  try {
    const fromInitData = tg?.initData
      ? new URLSearchParams(tg.initData).get('start_param')
      : null;
    if (fromInitData) {
      return fromInitData;
    }
  } catch {
    // ignore
  }
  return readStartParamFromUrl();
}

export function openApp(url) {
  if (isMax()) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  if (window.Telegram?.WebApp?.openLink) {
    window.Telegram.WebApp.openLink(url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function getUnavailableMessage() {
  return 'Telegram authentication data not available';
}
